import { ReactNode, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { isExclusionRequest } from "@signalist/shared-types";

import { InclusionStatus } from "@/enum/list.enum";

import { showSkippedLeadsToast } from "@/components/BlacklistOverride/blacklistOverride";
import { useBlacklistOverrideGate } from "@/components/BlacklistOverride/useBlacklistOverrideGate";
import { translateRequestError } from "@/utils/errors";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { useBulkChangeStatusMutation } from "@/pages/Private/redux/list/list.api";

import { BulkChangeStatus, ListPerson } from "../../../schema/list";

interface UseBlacklistAwareStatusChangeParams {
	/** The loaded rows; the gate asks about the blacklisted ones among the payload's leads. */
	data: ListPerson[];
}

interface BlacklistAwareStatusChange {
	/**
	 * `bulkChangeStatus(payload).unwrap()`, except that a move to Found or Unsure that would take
	 * blacklisted leads off the blacklist asks first. Resolves `false` when the user cancelled
	 * (nothing was sent) or the server refused the move (its sentence is toasted) and `true` once
	 * the move ran; the caller's dialog then closes instead of waiting on a rejection (PREC-4537).
	 */
	changeStatus: (payload: BulkChangeStatus) => Promise<boolean>;
	isLoading: boolean;
	/** Render this wherever the table renders its own modals. */
	leaveBlacklistModal: ReactNode;
}

/** The moves that take a blacklisted lead out of the Blacklist bucket. */
const LEAVES_BLACKLIST: readonly (InclusionStatus | undefined)[] = [
	InclusionStatus.ADDED,
	InclusionStatus.UNSURE,
];

/**
 * The list table's bulk status moves, with the blacklist override in front of them (PREC-2548).
 *
 * "Remove from Blacklist", "Add to list", "Exclude from list" and the reverts all write
 * `inclusionStatus`, and a move of a blacklisted lead to Found or Unsure takes it off the blacklist
 * — through the same server-side rules as the override modal. So the move asks with the same modal
 * ("remove" wording), passes the user's consent to remove a whole company along, and names every
 * lead the API kept on the blacklist, including the ones of a select-all beyond the loaded page.
 * Extracted from UnassignedTable, which may not grow.
 */
export const useBlacklistAwareStatusChange = ({
	data,
}: UseBlacklistAwareStatusChangeParams): BlacklistAwareStatusChange => {
	const { t } = useTranslation();
	const [bulkChangeStatus, { isLoading }] = useBulkChangeStatusMutation();
	const { gate, overrideModal } = useBlacklistOverrideGate();

	const changeStatus = useCallback(
		(payload: BulkChangeStatus): Promise<boolean> => {
			const movedIds = payload.assign.map(({ listPersonId }) => listPersonId);
			const rows = LEAVES_BLACKLIST.includes(payload.inclusionStatus)
				? data.filter((row) => movedIds.includes(row.id))
				: [];

			return gate(
				rows,
				async (_allowed, { removeCompany }) => {
					const result = await bulkChangeStatus({
						...payload,
						...(removeCompany && { removeCompany }),
					}).unwrap();

					showSkippedLeadsToast(result?.skippedFromBlacklist, t);

					// The server decides which leads the exclusion could take; name what it skipped
					// from its own answer (PREC-4537).
					const skipped = isExclusionRequest(payload)
						? (result?.skippedNotExcludable?.length ?? 0)
						: 0;

					if (skipped > 0) {
						showGlobalToast(t("inbox.excludeFromListSkipped", { count: skipped }), 8000);
					}
				},
				{ wording: "remove" }
			).catch((error: unknown) => {
				// A refusal made after the rows were loaded (an exclusion whose leads moved into a
				// campaign meanwhile) still says why; resolving `false` lets the caller close its
				// dialog instead of leaving it open on an unhandled rejection (PREC-4537).
				showGlobalToast(translateRequestError(error), 8000);

				return false;
			});
		},
		[data, gate, bulkChangeStatus, t]
	);

	return { changeStatus, isLoading, leaveBlacklistModal: overrideModal };
};
